import { isReadOnlyRole } from "auth/modules/util";
import { UserProfile } from "generated/graphql";
import { ClientApp } from "models";
import { APP_STATUS_LIVE, APP_STATUS_SUBMITTED_FOR_REVIEW } from "stentor-models";
import { ON_DEMAND_TEMPLATE, RADIO_TEMPLATE } from "constants/templates";

/**
 * Determines if an app has been submitted or not
 *
 * @export
 * @param {App} app
 * @returns {boolean}
 */
export function isAppSubmitted(app: Pick<ClientApp, "status">): boolean {
    const { status } = app;

    return app && app.status && status.type === APP_STATUS_SUBMITTED_FOR_REVIEW ? true : false;
}

/**
 * Determines if an App is live
 *
 * @export
 * @param {App} app
 * @returns {boolean}
 */
export function isAppLive(app: ClientApp): boolean {
    return app && app.status && app.status.type === APP_STATUS_LIVE;
}

/**
 * Determines whether or not to allow editing of a submitted app based on whether a user has admin privileges or not
 *
 * @export
 * @param {UserProfile} user
 * @returns {boolean}
 */
export function isDataMutable(user: UserProfile, app: Pick<ClientApp, "status" | "organizationId">): boolean {
    if (!user) {
        return false;
    }
    if (!app) {
        return false;
    }
    const userIsAdmin = user.isAdmin;
    const { organizationId } = app;

    if (isReadOnlyRole(organizationId, user)) {
        return false;
    }

    return isAppSubmitted(app) && !userIsAdmin ? false : true;
}

/**
 * Generates a standardized set of example phrases
 */
export function generateExamplePhrases(invocationName: string | string[]): string[] {
    return [`Alexa, open ${invocationName}`, `Alexa, pause`, `Alexa, resume`];
}

/**
 * Determines whether an app has at least 3 examplePhrases
 */
export function validateExamplePhrases(app: Pick<ClientApp, "examplePhrases" | "templateType">): boolean {
    const { examplePhrases, templateType } = app;

    if (templateType === ON_DEMAND_TEMPLATE || templateType === RADIO_TEMPLATE) {
        if (!examplePhrases || examplePhrases.length < 3) {
            return false;
        }
    }

    return true;
}
